import { Link } from "react-router-dom";
import { useApi } from "../lib/useApi";
import { formatDate, formatNumber, relativeTime } from "../lib/format";
import { AzureUnavailable, Badge, Card, ErrorState, PageHeader, Spinner, StatCard, Table, Td } from "../components/ui";

interface DashboardData {
  freeTools: {
    users: { total: number; paid: number; new_30d: number };
    certificates: { total: number; last_7d: number };
    signing: { total: number; last_24h: number; completed: number; internal: number };
    usageSeries: { day: string; events: number }[];
    topEvents: { event_type: string; count: number }[];
  };
  auth: {
    users: { total: number; new_30d: number; verified: number };
    activeSessions: number;
  };
  azure: {
    available: boolean;
    stats?: {
      users?: { total?: number; active?: number };
      organisations?: { total?: number };
      projects?: { total?: number; distinct_members?: number };
      audit_events_24h?: number;
      signing?: { requests_total?: number; usage_current_period?: number; billing_period?: string };
    };
    error?: string;
  };
  recentAudit: {
    admin_email: string;
    action: string;
    target_type: string | null;
    target_id: string | null;
    target_platform: string | null;
    created_at: string;
  }[];
}

function UsageSparkline({ series }: { series: { day: string; events: number }[] }) {
  if (series.length === 0) return <p className="text-sm text-slate-500">No usage events in the last 30 days.</p>;
  const max = Math.max(...series.map((s) => s.events), 1);
  return (
    <div className="flex h-24 items-end gap-0.5" role="img" aria-label="Daily usage events, last 30 days">
      {series.map((s) => (
        <div
          key={s.day}
          title={`${s.day}: ${s.events} events`}
          className="flex-1 rounded-t bg-rail-400 hover:bg-rail-600"
          style={{ height: `${Math.max((s.events / max) * 100, 4)}%` }}
        />
      ))}
    </div>
  );
}

export default function Dashboard() {
  const { data, loading, error, reload } = useApi<DashboardData>("/dashboard");

  if (loading) return <Spinner />;
  if (error || !data) return <ErrorState message={error ?? "Failed to load"} onRetry={reload} />;

  const azureStats = data.azure.stats ?? {};

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Aggregate view across the free tools, auth worker, and paid platform" />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard label="Auth accounts" value={formatNumber(data.auth.users.total)} hint={`${formatNumber(data.auth.users.new_30d)} new in 30d`} />
        <StatCard label="Free tools users" value={formatNumber(data.freeTools.users.total)} hint={`${formatNumber(data.freeTools.users.new_30d)} new in 30d`} />
        <StatCard label="Active sessions" value={formatNumber(data.auth.activeSessions)} hint="auth worker refresh tokens" />
        <StatCard label="Certificates" value={formatNumber(data.freeTools.certificates.total)} hint={`${formatNumber(data.freeTools.certificates.last_7d)} in last 7d`} />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard label="Signing requests" value={formatNumber(data.freeTools.signing.total)} hint={`${formatNumber(data.freeTools.signing.last_24h)} in last 24h`} />
        <StatCard label="Signing completed" value={formatNumber(data.freeTools.signing.completed)} hint={`${formatNumber(data.freeTools.signing.internal)} sealed internally`} />
        {data.azure.available ? (
          <>
            <StatCard
              label="Azure users"
              value={formatNumber(azureStats.users?.total ?? 0)}
              hint={`${formatNumber(azureStats.users?.active ?? 0)} active`}
            />
            <StatCard
              label="Azure projects"
              value={formatNumber(azureStats.projects?.total ?? 0)}
              hint={`${formatNumber(azureStats.organisations?.total ?? 0)} organisations`}
            />
          </>
        ) : (
          <div className="col-span-2">
            <AzureUnavailable detail={data.azure.error} />
          </div>
        )}
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <Card title="Free tools activity (30 days)">
          <UsageSparkline series={data.freeTools.usageSeries} />
          <div className="mt-3 flex flex-wrap gap-1.5">
            {data.freeTools.topEvents.map((e) => (
              <Badge key={e.event_type} tone="blue">
                {e.event_type}: {formatNumber(e.count)}
              </Badge>
            ))}
          </div>
        </Card>

        <Card
          title="Recent admin actions"
          actions={
            <Link to="/audit-log" className="text-xs text-rail-600 hover:underline">
              View all
            </Link>
          }
        >
          {data.recentAudit.length === 0 ? (
            <p className="text-sm text-slate-500">No admin actions recorded yet.</p>
          ) : (
            <Table headers={["Action", "Target", "Admin", "When"]}>
              {data.recentAudit.map((entry, i) => (
                <tr key={i}>
                  <Td className="font-mono text-xs">{entry.action}</Td>
                  <Td className="text-xs">
                    {entry.target_id ?? "—"}
                    {entry.target_platform && (
                      <span className="ml-1 text-slate-400">({entry.target_platform})</span>
                    )}
                  </Td>
                  <Td className="text-xs">{entry.admin_email}</Td>
                  <Td className="text-xs" title={formatDate(entry.created_at)}>
                    {relativeTime(entry.created_at)}
                  </Td>
                </tr>
              ))}
            </Table>
          )}
        </Card>
      </div>
    </div>
  );
}
